/* eslint-disable max-lines-per-function */
/* eslint-disable no-nested-ternary */
/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { type Insight as BaseInsight, registerStandardChartInsight } from '../insightManager';
import { toArray } from '../../util/data';
import {
  findSeriesDataItemInfo,
  getSingletonInsightDvMarkerSeries,
  mergeSeriesDataItem,
  getDataItemValue
} from '../../helper/standardChart';
import {
  findExtend,
  parsePercent,
  handleMultipleSeries,
  handleAnimation,
  isMultipleSeriesWithoutDvMarker
} from '../helper';

type InsightData = {
  /** x 轴指标对应数据集字段 */
  x: string;
  /** y 轴指标对应数据集字段 */
  y: string;
  /** 要标记的目标点 x 轴指标数据值 */
  xCompare: string;
  /** 要标记的对比点 x 轴指标数据值 */
  xCompared: string;
  /** 要标记的目标点 y 轴指标数据值 */
  yCompare?: string;
  /** 要标记的对比点 y 轴指标数据值 */
  yCompared?: string;
  /** 对比点所在的系列名称 */
  compareSeries?: string;
  /** 被对比点所在的系列名称 */
  comparedSeries?: string;
  /** 对比点的文本描述 */
  xComparedSummary: string;
  /** 点的文本描述 */
  summary: string;
  /** 文本摘要 @see https://datav.iwencai.com/components/paradigm-chart/docs/apis/series/dvMarker/#seriesdvitemstyle */
  textSummary?: unknown;
  /** 对比值线 @see https://echarts.apache.org/zh/option.html#series-bar.markLine.data */
  line?: unknown;
  /** 点与线之间的参考线 @see https://echarts.apache.org/zh/option.html#series-bar.markLine.data */
  referenceLine?: unknown;
  /** 点与轴之间的参考线 @see https://echarts.apache.org/zh/option.html#series-bar.markLine.data */
  axisReferenceLine?: unknown;
  /** 弱化系列 */
  blurSeries?: unknown;
  /** 开启箭头隐藏的百分比 */
  hideArrowThreshold?: number | string;
};

const type = 'ComparePoint';
const INSIGHT_FLAG = `insight_${type}`;

interface Insight extends BaseInsight<InsightData[]> {
  readonly type: typeof type;
}

registerStandardChartInsight<Insight>({
  type,
  filter: option => {
    return (
      toArray(option.xAxis).length > 0 &&
      toArray(option.yAxis).length > 0 &&
      !toArray(option.series).some(item => !['line', 'bar', 'scatter'].includes(item.type))
    );
  },
  clear: {
    onOptionUpdate(option) {
      option.series = toArray(option.series).map(series => {
        series.markPoint = series.markPoint || {};
        series.markPoint.data = (series.markPoint.data || []).filter(
          item => item.__insightFlag !== INSIGHT_FLAG
        );
        series.markLine = series.markLine || {};
        series.markLine.data = (series.markLine.data || []).filter(
          item => item.__insightFlag !== INSIGHT_FLAG
        );

        return series;
      });
      if (option?.legend?.data) {
        option.legend.data = option.legend.data?.map(legendItem => {
          delete (legendItem as { __blur?: number }).__blur;
          return legendItem;
        });
      }
    }
  },
  render: {
    onOptionUpdate(context, option, legendIns) {
      const insight = context.insightOption;
      const { linkLegend = false } = insight;
      const { animationDuration = 0 } = insight;
      const isMultipleSeries = isMultipleSeriesWithoutDvMarker(option);

      function render(data: InsightData) {
        const {
          x,
          y,
          xCompare,
          xCompared,
          yCompare,
          yCompared,
          compareSeries,
          comparedSeries: comparedSeriesName,
          xComparedSummary,
          summary,
          textSummary: textSummaryOpt = {},
          line: lineOpt = {},
          referenceLine: referenceLineOpt = {},
          axisReferenceLine: axisReferenceLineOpt = {},
          blurSeries = {},
          hideArrowThreshold = 0.1
        } = data;
        const blurSeriesOpt = JSON.parse(JSON.stringify(blurSeries));
        const pointDataItemMetaInfo = findSeriesDataItemInfo(option, {
          xField: x,
          yField: y,
          xValue: xCompare,
          yValue: yCompare,
          seriesName: compareSeries
        });
        if (!pointDataItemMetaInfo) {
          return;
        }
        const comparedPointDataItemMetaInfo = findSeriesDataItemInfo(option, {
          xField: x,
          yField: y,
          xValue: xCompared,
          yValue: yCompared,
          seriesName: comparedSeriesName
        });
        if (!comparedPointDataItemMetaInfo) {
          return;
        }
        const { xAxisIndex, yAxisIndex, seriesIndex, dataItemIndex } = pointDataItemMetaInfo;
        const { dataItemIndex: comparedDataItemIndex, seriesIndex: comparedSeriesIndex } =
          comparedPointDataItemMetaInfo;
        const series = context.util.data.toArray(option.series)[seriesIndex];
        const comparedSeries = context.util.data.toArray(option.series)[comparedSeriesIndex];
        const extend = findExtend(series.data);
        const tempDataValue = getDataItemValue(series.data[dataItemIndex]);
        // 适配散点图（data中的value为数组）
        const dataValue = (tempDataValue as [number, number])[1] || tempDataValue;
        const tempComparedDataValue = getDataItemValue(comparedSeries.data[comparedDataItemIndex]);
        const comparedDataValue =
          (tempComparedDataValue as [number, number])[1] || tempComparedDataValue;
        const pointDiffRatio =
          Math.abs(+dataValue - +comparedDataValue) / (+extend[1] - +extend[0]);

        series.markPoint = series.markPoint || {};
        series.markPoint.data = series.markPoint.data || [];
        series.markLine = series.markLine || {};
        series.markLine.data = series.markLine.data || [];
        comparedSeries.markLine = comparedSeries.markLine || {};
        comparedSeries.markLine.data = comparedSeries.markLine.data || [];

        const comparedLine = {
          ...context.util.data.deepMerge(lineOpt, {
            // ! bugs @see https://github.com/apache/echarts/issues/18433
            symbol: 'none',
            dvSymbolNone: true,
            label: {
              position: 'insideStartTop'
            }
          }),
          // ! name 在该模式下不生效
          name: xComparedSummary,
          dvLabelText: xComparedSummary,
          yAxis: comparedDataValue,
          __insightFlag: INSIGHT_FLAG
        };
        const referenceLine = [
          {
            ...context.util.data.deepMerge(
              referenceLineOpt,
              {
                symbol:
                  pointDiffRatio < parsePercent(hideArrowThreshold, +extend[1] - +extend[0])
                    ? 'none'
                    : (referenceLineOpt as { symbol: string }).symbol
                    ? (referenceLineOpt as { symbol: string }).symbol
                    : 'arrow'
              },
              { override: true }
            ),
            coord: [xCompare, dataValue]
          },
          {
            ...context.util.data.deepMerge(
              referenceLineOpt,
              {
                symbol:
                  pointDiffRatio < parsePercent(hideArrowThreshold, +extend[1] - +extend[0])
                    ? 'none'
                    : (referenceLineOpt as { symbol: string }).symbol
                    ? (referenceLineOpt as { symbol: string }).symbol
                    : 'arrow'
              },
              { override: true }
            ),
            coord: [xCompare, comparedDataValue]
          }
        ];
        (referenceLine as unknown as { __insightFlag: string }).__insightFlag = INSIGHT_FLAG;
        const pointAxisReferenceLine = [
          {
            ...context.util.data.deepMerge(axisReferenceLineOpt, {
              symbol: 'circle'
            }),
            coord: [xCompare, dataValue]
          },
          {
            ...context.util.data.deepMerge(axisReferenceLineOpt, {
              symbol: 'none',
              name: xCompare
            }),
            coord: [xCompare, dataValue],
            dvGridY: '100%'
          }
        ];
        (pointAxisReferenceLine as unknown as { __insightFlag: string }).__insightFlag =
          INSIGHT_FLAG;
        const comparedPointAxisReferenceLine = [
          {
            ...context.util.data.deepMerge(axisReferenceLineOpt, {
              symbol: 'none',
              name: xCompared,
              label: {
                position: 'start'
              }
            }),
            coord: [xCompared, comparedDataValue],
            dvGridY: '100%'
          },
          {
            ...context.util.data.deepMerge(axisReferenceLineOpt, {
              symbol: 'none'
            }),
            coord: [xCompared, comparedDataValue]
          }
        ];
        (comparedPointAxisReferenceLine as unknown as { __insightFlag: string }).__insightFlag =
          INSIGHT_FLAG;
        const comparedPointAxisReferenceLine1 = [
          {
            ...context.util.data.deepMerge(lineOpt, {
              symbol: 'none'
            }),
            coord: [xCompared, comparedDataValue]
          },
          {
            ...context.util.data.deepMerge(lineOpt, {
              symbol: 'circle'
            }),
            coord: [xCompared, comparedDataValue]
          }
        ];
        (comparedPointAxisReferenceLine1 as unknown as { __insightFlag: string }).__insightFlag =
          INSIGHT_FLAG;

        series.markLine.data.push(referenceLine, pointAxisReferenceLine);
        comparedSeries.markLine.data.push(
          comparedLine,
          comparedPointAxisReferenceLine,
          comparedPointAxisReferenceLine1
        );

        const markerSeries = getSingletonInsightDvMarkerSeries(option, { xAxisIndex, yAxisIndex });
        const pointSummary = {
          value: [xCompare, dataValue],
          __seriesIndex: seriesIndex,
          itemStyle: context.util.data.deepMerge(
            {
              textStyle: {
                style: {
                  text: summary
                }
              }
            },
            textSummaryOpt
          )
        };

        markerSeries.data.push(pointSummary as unknown as number);
        handleAnimation(series.markLine, animationDuration, markerSeries);

        // * 弱化主图
        if (series.type === 'bar') {
          series.data = series.data.map((dataItem, index) => {
            if (
              [dataItemIndex, comparedDataItemIndex].includes(index) ||
              (dataItem as unknown as { __noBlur?: boolean })?.__noBlur
            ) {
              return mergeSeriesDataItem(dataItem, { __noBlur: true });
            }

            return mergeSeriesDataItem(dataItem, blurSeriesOpt);
          });
        }
        if (series.type === 'line') {
          option.series = context.util.data.toArray(option.series);
          if (!option.series[seriesIndex].areaStyle) {
            delete blurSeriesOpt.areaStyle;
          }
          if (isMultipleSeries) {
            // TODO 多系列下柱状图的弱化效果不同
            handleMultipleSeries(
              option,
              seriesIndex,
              legendIns!,
              blurSeriesOpt,
              linkLegend,
              comparedSeriesIndex
            );
            return;
          }
          option.series[seriesIndex] = context.util.data.deepMerge(series, blurSeriesOpt);
        }
      }

      insight.data.forEach(item => render(item));
    }
  }
});
